/** Shapes returned by the API (dates arrive as ISO strings). */

export type Role = "owner" | "admin" | "agent";
export type ConversationStatus = "bot" | "human" | "closed";
export type DeliveryStatus = "received" | "pending" | "sent" | "delivered" | "read" | "failed";
export type ContactType = "consumer" | "business" | "institution";

export interface Me {
  user: { id: string; email: string; name: string; twoFactorEnabled?: boolean | null };
  tenants: { tenantId: string; tenantName: string; role: Role }[];
  activeTenantId: string | null;
  requireTwoFactor: boolean;
}

export interface Invitation {
  id: string;
  email: string;
  role: Role;
  expiresAt: string;
  state: "pending" | "accepted" | "revoked" | "expired";
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: Role;
}

export interface ConversationSummary {
  id: string;
  status: ConversationStatus;
  phoneNumberId: string;
  assigneeId: string | null;
  lastInboundAt: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  windowOpen: boolean;
  contact: { id: string; waId: string; name: string | null; tags: string[] };
}

export interface ConversationEvent {
  id: string;
  type: "assigned" | "unassigned" | "status_changed";
  actorId: string | null;
  data: Record<string, unknown> | null;
  createdAt: string;
}

export interface ConversationDetail extends Omit<ConversationSummary, "contact"> {
  handoffReason: string | null;
  contact: Contact;
  events: ConversationEvent[];
}

export interface Message {
  id: string;
  direction: "inbound" | "outbound";
  author: "customer" | "agent_ai" | "agent_human" | "system";
  authorUserId: string | null;
  type: string;
  body: string | null;
  /** Why WhatsApp did not send it (Meta's error code; 0: WhatsApp not reached), when it failed. */
  failureCode: number | null;
  /** A document sent from Orbis (a quote's PDF). */
  document: { filename: string; quoteId: string | null } | null;
  wamid: string | null;
  deliveryStatus: DeliveryStatus;
  sentAt: string;
}

export interface Contact {
  id: string;
  waId: string;
  name: string | null;
  type: ContactType | null;
  tags: string[];
  province: string | null;
  email: string | null;
  notes: string | null;
  organizationId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContactCard extends Contact {
  organization: { id: string; name: string } | null;
  conversations: {
    id: string;
    status: ConversationStatus;
    assigneeId: string | null;
    lastMessageAt: string | null;
    lastMessagePreview: string | null;
    createdAt: string;
  }[];
}

export type ServiceModality = "local" | "home" | "online";

export interface Product {
  id: string;
  /** part: a spare part or accessory for the products in `fits`. */
  kind: "product" | "service" | "part";
  name: string;
  model: string | null;
  category: string | null;
  description: string | null;
  specs: { label: string; value: string }[];
  /** Uploaded photos have an id (their files are in Orbis); links do not. */
  images: { url: string; alt?: string; id?: string }[];
  url: string | null;
  warrantyMonths: number | null;
  spareParts: boolean;
  /** Parts only: the products it fits (ids). */
  fits: string[];
  durationMinutes: number | null;
  modalities: ServiceModality[];
  price: number | null;
  priceBusiness: number | null;
  priceInstitution: number | null;
  currency: "CRC" | "USD";
  priceUnit: string | null;
  showPrice: boolean;
  /** inactive: Lumi does not offer it. */
  status: "active" | "inactive";
  internalNotes: string | null;
  externalId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Another catalog item, as the product page links it. */
export interface ProductLink {
  id: string;
  name: string;
  model: string | null;
  status: Product["status"];
}

/** The product page: a part with the products it fits, a product with its parts. */
export interface ProductDetail extends Product {
  fitsProducts: ProductLink[];
  parts: ProductLink[];
}

export type KnowledgeKind =
  | "warranty"
  | "shipping"
  | "hours"
  | "service"
  | "faq"
  | "manual"
  | "tutorial"
  | "policy"
  | "other";

export interface KnowledgeDoc {
  id: string;
  title: string;
  kind: KnowledgeKind;
  body: string;
  productIds: string[];
  visibleToAi: boolean;
  fileKey: string | null;
  fileName: string | null;
  fileType: string | null;
  fileSize: number | null;
  createdAt: string;
  updatedAt: string;
}

/** The list leaves the text out and sends its length. */
export type KnowledgeListItem = Omit<KnowledgeDoc, "body"> & {
  bodyLength: number;
  /** "pending" while the IA's index catches up; "error" if it kept failing. */
  indexStatus: "indexed" | "pending" | "error";
};

export interface SalesPoint {
  id: string;
  name: string;
  kind: "store" | "reseller" | "service";
  province: string | null;
  canton: string | null;
  address: string | null;
  hours: string | null;
  phone: string | null;
  mapsUrl: string | null;
  wazeUrl: string | null;
  services: string | null;
  visibleToAi: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SearchHit {
  id: string;
  sourceType: "knowledge" | "product" | "sales_point";
  sourceId: string;
  title: string;
  text: string;
  score: number;
  match: "semantic" | "model" | "words";
}

export interface CompanyProfile {
  phone: string | null;
  address: string | null;
  /** In words; written from `schedule` when there is one. */
  hours: string | null;
  website: string | null;
  email: string | null;
  /** Printed on quotes. */
  legalName: string | null;
  legalId: string | null;
  schedule: import("@orbis/shared").Schedule | null;
  updatedAt: string | null;
}

export interface Organization {
  id: string;
  name: string;
  legalId: string | null;
  contactCount?: number;
}

export interface QuickReply {
  id: string;
  shortcut: string;
  title: string;
  body: string;
  category: string | null;
}

export interface Template {
  id: string;
  wabaId: string;
  name: string;
  language: string;
  status: string;
  category: string;
  headerText: string | null;
  bodyText: string;
  footerText: string | null;
  bodyVariables: string[];
  headerVariables: string[];
  named: boolean;
  supported: boolean;
  /** Meta's reason when it rejected it. */
  rejectedReason: string | null;
  hidden: boolean;
  variableMap: import("@orbis/shared").TemplateVariableMap;
  syncedAt: string;
}

export const PROVINCES = ["San José", "Alajuela", "Cartago", "Heredia", "Guanacaste", "Puntarenas", "Limón"] as const;

/** Lumi's messages left (owners and admins only). Dates are ISO strings. */
export interface LumiBalance {
  plan: { name: string; monthlyMessages: number; startsAt: string; endsAt: string; active: boolean } | null;
  period: { start: string; end: string } | null;
  monthUsed: number;
  monthLeft: number;
  topupLeft: number;
  total: number;
  renewsAt: string | null;
}

export interface AiStatus {
  enabled: boolean;
  balance: LumiBalance | null;
}

/** What the Lumi section edits (GET/PUT /api/ai/config). */
export interface LumiConfig {
  businessDescription: string | null;
  topics: string | null;
  tone: "cercano" | "formal";
  assistantName: string | null;
  address: "usted" | "tu" | "vos";
  emojiUse: "never" | "mirror" | "some";
  replyLength: "short" | "normal" | "detailed";
  instructions: string | null;
  offerType: "catalog" | "custom" | "mixed";
  customWeDo: string | null;
  customWeDont: string | null;
  customNeedToKnow: string | null;
  recommendScope: "catalog_only" | "complements" | "open";
  neverRecommend: string | null;
  maySuggest: string | null;
  /** When the guided setup was finished; null offers it. */
  setupCompletedAt: string | null;
  handoffRules: string | null;
  refusalMessage: string | null;
  handoffMessage: string | null;
  pricePolicy: "no_share" | "public" | "by_customer_type";
  greeting: string | null;
  collectFields: ("name" | "location" | "customer_type" | "email")[];
  collectMode: "off" | "first" | "strict" | "natural";
  analysisMode: "auto" | "fast" | "balanced" | "detailed";
  postSaleHelp: "answer" | "handoff";
  caseLookup: boolean;
  postSaleInstructions: string | null;
  offHoursMode: "lumi" | "message";
  offHoursMessage: string | null;
}

export type LumiTestResult =
  | {
      status: "ok";
      reply: string;
      handoff: boolean;
      contactUpdates: Record<string, string>;
      testsLeft: number;
    }
  | { status: "daily_limit" | "no_balance"; testsLeft: number };

export type BusinessKind = "products" | "services" | "custom" | "mixed";

/** What "Descríbeme tu negocio" proposes; every field may be missing. */
export interface SetupProposal {
  businessKind?: BusinessKind;
  description?: string;
  topics?: string;
  /** Usted, tú or vos. */
  treatment?: LumiConfig["address"];
  tone?: LumiConfig["tone"];
  pricePolicy?: LumiConfig["pricePolicy"];
  customWeDo?: string;
  customWeDont?: string;
  customNeedToKnow?: string;
  greeting?: string;
  phone?: string;
  email?: string;
  /** The company's street address. */
  address?: string;
}

// ---- After-sales cases ----------------------------------------------------

export type CaseKind = "warranty" | "repair" | "exchange" | "return" | "other";

export interface CaseStatus {
  id: string;
  label: string;
  position: number;
  closed: boolean;
  /** Message the panel suggests sending when a case reaches this status. */
  notice: string | null;
}

export interface CaseSummary {
  id: string;
  number: number;
  title: string;
  kind: CaseKind;
  statusId: string;
  statusLabel: string;
  closed: boolean;
  assigneeId: string | null;
  contactId: string;
  contactName: string | null;
  contactWaId: string;
  productName: string | null;
  model: string | null;
  reference: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CaseEvent {
  id: string;
  type: "created" | "status" | "assigned" | "edited" | "note";
  actorId: string | null;
  text: string | null;
  data: Record<string, unknown> | null;
  createdAt: string;
}

export interface CaseCard {
  id: string;
  number: number;
  contactId: string;
  conversationId: string | null;
  productId: string | null;
  kind: CaseKind;
  statusId: string;
  title: string;
  description: string | null;
  model: string | null;
  serial: string | null;
  purchaseDate: string | null;
  purchasePlace: string | null;
  invoice: string | null;
  reference: string | null;
  assigneeId: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  status: CaseStatus;
  contact: { id: string; name: string | null; waId: string };
  product: { id: string; name: string; model: string | null } | null;
  /** The part requests the case waits for. */
  partRequests: PartRequestSummary[];
  events: CaseEvent[];
}

// ---- Quotes ---------------------------------------------------------------------

export type QuoteStatus = "draft" | "sent" | "accepted" | "rejected";

export interface QuoteSettings {
  /** Catalog prices already include the tax, or it is added on top. */
  pricesIncludeTax: boolean;
  taxRate: number;
  validDays: number;
  terms: string | null;
  /** The company's letterhead: every page of the PDF is drawn on it. */
  letterhead: { key: string; type: "pdf" | "png" | "jpg"; name: string } | null;
  /** Space its header and footer take, in millimeters. */
  marginTopMm: number;
  marginBottomMm: number;
  printCompany: boolean;
}

export interface QuoteLine {
  productId: string | null;
  description: string;
  model: string | null;
  quantity: number;
  unitPrice: number;
  /** Percent of the line. */
  discount: number;
}

export interface QuoteTotals {
  gross: number;
  discount: number;
  subtotal: number;
  tax: number;
  total: number;
}

export interface QuoteSummary {
  id: string;
  number: number;
  status: QuoteStatus;
  /** Sent and past its date. */
  expired: boolean;
  contactId: string;
  contactName: string | null;
  contactWaId: string;
  currency: "CRC" | "USD";
  total: number;
  lineCount: number;
  firstLine: string | null;
  validUntil: string;
  reference: string | null;
  assigneeId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteCard {
  id: string;
  number: number;
  contactId: string;
  conversationId: string | null;
  status: QuoteStatus;
  expired: boolean;
  currency: "CRC" | "USD";
  pricesIncludeTax: boolean;
  taxRate: number;
  validUntil: string;
  terms: string | null;
  reference: string | null;
  assigneeId: string | null;
  sentAt: string | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
  contact: { id: string; name: string | null; waId: string; type: Contact["type"]; email: string | null };
  lines: (QuoteLine & { id: string })[];
  totals: QuoteTotals;
}

// ---- Spare-part requests -------------------------------------------------------

export interface PartRequestStatus extends CaseStatus {
  /** The part is in: the case waiting for it can move on. */
  arrived: boolean;
}

export interface PartRequestSummary {
  id: string;
  number: number;
  partName: string;
  partModel: string | null;
  quantity: number;
  statusId: string;
  statusLabel: string;
  closed: boolean;
  arrived: boolean;
  assigneeId: string | null;
  contactId: string;
  contactName: string | null;
  contactWaId: string;
  caseId: string | null;
  productName: string | null;
  model: string | null;
  reference: string | null;
  /** When it reached its current status. */
  statusAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface PartRequestCard {
  id: string;
  number: number;
  contactId: string;
  conversationId: string | null;
  caseId: string | null;
  partId: string | null;
  partName: string;
  productId: string | null;
  model: string | null;
  quantity: number;
  statusId: string;
  description: string | null;
  reference: string | null;
  assigneeId: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  status: PartRequestStatus;
  contact: { id: string; name: string | null; waId: string };
  part: { id: string; name: string; model: string | null } | null;
  product: { id: string; name: string; model: string | null } | null;
  case: { id: string; number: number; title: string; statusId: string } | null;
  events: CaseEvent[];
}

// ---- Team: visibility and areas ----------------------------------------------

export interface TeamSettings {
  /** Advisors see everything, or only what is assigned to them. */
  agentVisibility: "all" | "own";
}

export interface TeamArea {
  id: string;
  name: string;
  /** When it applies, in the company's words: what Lumi reads to choose it. */
  description: string;
  memberIds: string[];
}

// ---- Notifications ------------------------------------------------------------

export type NotificationKind = "message" | "assigned" | "unassigned";

export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  conversationId: string;
  /** Messages it stands for (kind "message"). */
  count: number;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
  contactName: string | null;
  contactWaId: string;
  preview: string | null;
}

// ---- Reports (f4-04) -------------------------------------------------------------

export type HandoffReason =
  | "ai_handoff"
  | "ai_check"
  | "ai_error"
  | "ai_limit"
  | "ai_no_balance"
  | "off_hours"
  | "agent_reply"
  | "manual";

export interface ReportPeriod {
  conversations: {
    started: number;
    inbound: number;
    handoffs: number;
    byReason: Partial<Record<HandoffReason | "other", number>>;
    /** Null for an advisor's own report. */
    lumi: { answered: number; solvedAlone: number; messages: number } | null;
  };
  team: {
    agents: {
      userId: string;
      conversations: number;
      replies: number;
      medianWaitMinutes: number | null;
      within15: number | null;
    }[];
    medianWaitMinutes: number | null;
    areas: Record<string, number> | null;
  };
  postSale: {
    cases: {
      openNow: number;
      opened: number;
      closed: number;
      averageDaysToClose: number | null;
      byStatus: { status: string; averageDays: number; count: number }[];
    };
    parts: { requested: number; pendingNow: number; arrived: number; averageDaysToArrive: number | null };
  };
  sales: {
    created: number;
    sent: number;
    accepted: number;
    rejected: number;
    sentAmount: { CRC: number; USD: number };
    acceptedAmount: { CRC: number; USD: number };
    closeRate: number | null;
    openNow: number;
    topProducts: { label: string; quantity: number; quotes: number }[];
  };
}

export interface Report {
  scope: "all" | "own";
  days: number;
  current: ReportPeriod;
  previous: ReportPeriod;
  waitingNow: number;
}
